import "../styles/dashboard.css";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AgricultureIcon from '@mui/icons-material/Agriculture';
import EqualizerSharpIcon from '@mui/icons-material/EqualizerSharp';
import InventorySharpIcon from '@mui/icons-material/InventorySharp';
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useEffect, useState, useRef } from "react";
import Button from "../components/Button";
import Select from "react-select";
import api from "../api/api";
 
// Charts
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
  CartesianGrid,
  LabelList,
} from "recharts";
 
const Dashboard = () => {
  // ===== Hooks for date filter =====
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [harvestVsSales, setHarvestVsSales] = useState([]);
  const [revenueTrend, setRevenueTrend] = useState([]);
  const [topCrops, setTopCrops] = useState([]);
 
  const fetchCategories = async () => {
    try {
      const res = await api.get("/categories");
 
      const formatted = res.data.categories.map((cat) => ({
        value: cat.id,
        label: cat.category_name,
      }));
 
      setCategories(formatted);
    } catch (error) {
      console.error("Category fetch error:", error);
    }
  };
 
  useEffect(() => {
    fetchDashboardData();
    fetchCategories();
  }, []);
 
  // Temporary user definition to prevent ESLint errors
  // eslint-disable-next-line no-unused-vars
  const [user] = useState(true);
  const CHART_HEIGHT = 250;
 
  // ===== Dashboard State =====
  const [kpi, setKpi] = useState({
    totalHarvest: 0,
    totalSales: 0,
    revenue: 0,
    inventory: 0,
    purchases: 0,
    grossMargin: 0,
  });
 
  const [revenueFilter] = useState("daily");
 
  const formatNumber = (num) => {
    if (num >= 1e12) return (num / 1e12).toFixed(1) + "T";
    if (num >= 1e9) return (num / 1e9).toFixed(1) + "B";
    if (num >= 1e6) return (num / 1e6).toFixed(1) + "M";
    if (num >= 1e3) return (num / 1e3).toFixed(1) + "K";
    return num;
  };
 
  const fetchDashboardData = async () => {
    try {
      const res = await api("/dashboard/kpis");
      setKpi(res.data.data);
    } catch (error) {
      console.error("Dashboard API Error:", error);
    }
  };
 
  const fetchKpis = async (start, end, categoryId) => {
    try {
      const params = {};
      if (start) params.startDate = start.toISOString();
      if (end) params.endDate = end.toISOString();
      if (categoryId) params.categoryId = categoryId;
 
      const res = await api.get("/dashboard/kpis", { params });
      setKpi(res.data.data);
    } catch (error) {
      console.error("KPI API Error:", error);
    }
  };
 
  const fetchHarvestVsSales = async (start, end, categoryId) => {
    try {
      const params = {};
      if (start) params.startDate = start.toISOString();
      if (end) params.endDate = end.toISOString();
      if (categoryId) params.categoryId = categoryId;
 
      const res = await api.get("/dashboard/harvest-vs-sales", { params });
      setHarvestVsSales(res.data.data);
    } catch (error) {
      console.error("Harvest vs Sales API Error:", error);
    }
  };
 
  const fetchRevenueTrade = async (
    start,
    end,
    type = revenueFilter,
    categoryId,
  ) => {
    try {
      const params = { type };
      if (start) params.startDate = start.toISOString();
      if (end) params.endDate = end.toISOString();
      if (categoryId) params.categoryId = categoryId;
 
      const res = await api.get("/dashboard/revenue-trend", { params });
      setRevenueTrend(res.data.data);
    } catch (error) {
      console.error("Revenue Trend API Error:", error);
    }
  };
 
  const fetchTopCropsData = async (start, end, categoryId) => {
    try {
      const params = {};
      if (start) params.startDate = start.toISOString();
      if (end) params.endDate = end.toISOString();
      if (categoryId) params.categoryId = categoryId;
 
      const res = await api.get("/dashboard/top-crops", { params });
 
      if (res.data.success) {
        const formatted = res.data.data
          .map((item) => ({ ...item, revenue: Number(item.revenue) }))
          .filter((item) => item.revenue > 0);
        setTopCrops(formatted);
      } else {
        setTopCrops([]);
      }
    } catch (error) {
      console.error("Top Crops API Error:", error);
      setTopCrops([]);
    }
  };
 
  useEffect(() => {
    fetchDashboardData();
    fetchHarvestVsSales();
    fetchTopCropsData();
    fetchRevenueTrade();
  }, []);
  // Filter topCrops to remove zero revenue
  const filteredTopCrops = topCrops.filter((item) => item.revenue > 0);
 
  // ===== Top 5 crops=====
  // const topCrops = [...cropData]
  //   .sort((a, b) => b.revenue - a.revenue)
  //   .slice(0, 5);
 
  // ===== KPI Cards =====
  const cards = [
    {
      title: "Total Harvest Volume",
      value: `${kpi.totalHarvest}`,
      icon: <AgricultureIcon  className="card-icon" />,
    },
    {
      title: "Total Sales Volume",
      value: `${kpi.totalSales}`,
      icon: <EqualizerSharpIcon className="card-icon sales" />,
    },
    {
      title: "Revenue Generated",
      value: `₹${kpi.revenue}`,
      icon: <MonetizationOnIcon className="card-icon pending" />,
    },
    {
      title: "Inventory Available",
      value: `${kpi.inventory}`,
      icon: <InventorySharpIcon className="card-icon" />,
    },
    {
      title: "Farm Input Purchases",
      value: `₹${kpi.purchases}`,
      icon: <ShoppingCartIcon className="card-icon purchase" />,
    },
    {
      title: "Gross Margin Estimate",
      value: `₹${kpi.grossMargin}`,
      icon:<TrendingUpIcon className="card-icon pending" />,
    },
  ];
 
  const getInventoryColor = () => {
    if (kpi.inventory > 50) return "#f44336"; // Red → Overstock
    if (kpi.inventory > 20) return "#FFC107"; // Yellow → Balanced
    return "#4CAF50"; // Green → Understock
  };
 
  // ===== Date Filter Handlers =====
  const handleFilter = () => {
    const start = startDate;
    const end = endDate;
    const categoryId = selectedCategory ? selectedCategory.value : null;
 
    // Fetch KPIs
    fetchKpis(start, end, categoryId);
 
    // Fetch Harvest vs Sales
    fetchHarvestVsSales(start, end, categoryId);
 
    // Fetch Revenue Trend
    fetchRevenueTrade(start, end, revenueFilter, categoryId);
 
    // Fetch Top Crops
    fetchTopCropsData(start, end, categoryId);
 
    setOpen(false);
  };
 
  const handleClearFilter = () => {
    setDateRange([null, null]);
    setSelectedCategory(null);
 
    // Fetch all data without filters
    fetchKpis();
    fetchHarvestVsSales();
    fetchRevenueTrade(null, null, revenueFilter);
    fetchTopCropsData();
 
    setOpen(false);
  };
 
  const handleToggle = () => {
    setOpen((prev) => !prev);
  };
 
 
const customSelectStyles = {
  control: (provided) => ({
    ...provided,
    borderRadius: "8px",
    border: "1px solid #ced4da",
    minHeight: "40px",
    height: "40px",
    fontSize: "14px",
    display: "flex",
    alignItems: "center",
    color: "#212529",  
  }),
 
  valueContainer: (provided) => ({
    ...provided,
    padding: "0 10px",
    height: "40px",
    display: "flex",
    alignItems: "center",  
    color: "#ced4da",
  }),
 
  input: (provided) => ({
    ...provided,
    margin: "0px",
    padding: "0px",
    fontSize: "14px",
    color: "#ced4da",
  }),
 
  singleValue: (provided) => ({
    ...provided,
    fontSize: "14px",
    margin: "0px",
    color: "#ced4da",
  }),
 
  placeholder: (provided) => ({
    ...provided,
    fontSize: "14px",
    margin: "0px",
   
  }),
 
  indicatorsContainer: (provided) => ({
    ...provided,
    height: "40px",
  }),
};
 
 
  if (!user) return <div>Loading...</div>;
 
  return (
    <>
    <div className="dashboard-container">
      {/* ===== DATE FILTER ===== */}
      <div className="row align-items-center mb-4">
        <div className="col-sm-6 col-md-3">
          <input
            ref={inputRef}
            readOnly
            value={
              startDate && endDate
                ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
                : ""
            }
            onClick={handleToggle}
            placeholder="Select Date Range"
            className="form-control"
          />
          {open && (
            <div className="position-absolute bg-white shadow p-3 rounded mt-1 z-3">
              <DatePicker
                inline
                selectsRange
                monthsShown={2}
                startDate={startDate}
                endDate={endDate}
                onChange={(update) => setDateRange(update)}
                maxDate={new Date()}
              />
              <div className="d-flex justify-content-end mt-2">
                <Button onClick={handleToggle} text="Done" btncls="me-2" />
                <Button
                  onClick={handleClearFilter}
                  text="Clear"
                  variant="danger"
                />
              </div>
            </div>
          )}
        </div>
     
 
<div className="col-sm-6 col-md-3 mt-3 mt-sm-0">
  <Select
    styles={customSelectStyles}
    options={categories}
    value={selectedCategory}
    onChange={(option) => setSelectedCategory(option)}
    placeholder="Select Category"
    isClearable
  />
</div>
        <div className="col-sm-6 col-md-4 col-lg-5 col-xl-3 d-flex mt-3 mt-sm-0">
          <Button
            onClick={handleFilter}
            text="Apply Filter"
            btncls="me-2 mt-0 w-100 w-sm-auto"
          />
          <Button
            onClick={handleClearFilter}
            text="Clear Filter"
            variant="secondary"
            btncls="mt-0 bg-secondary w-100"
          />
        </div>
      </div>
 
      <div className="dashboard-container">
        {/* ===== KPI CARDS ===== */}
        <div className="dashboard-cards">
          {cards.map((card, index) => (
            <div className="dashboard-card" key={index}>
              <div className="card-header">
                <p className="card-title">{card.title}</p>
                {card.icon}
              </div>
              <h3 className="card-value">{card.value}</h3>
            </div>
          ))}
        </div>
 
        {/* ===== VISUAL WIDGETS ===== */}
        <div className="dashboard-widgets">
          {/* Harvest vs Sales */}
          <div className="widget">
            <h2 className="h22">Harvest vs Sales</h2>
            <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
              {/* Change chartData to harvestVsSales here */}
              <LineChart data={harvestVsSales}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(tick) => {
                    // If the date comes as an ISO string, format it for the UI
                    return tick
                      ? new Date(tick).toLocaleDateString("en-IN", {
                          day: "numeric",
                          month: "short",
                        })
                      : "";
                  }}
                />
                <YAxis />
                {/* Updated Tooltip to show volume instead of Currency (since it's quantity) */}
                <Tooltip formatter={(val) => `${val.toLocaleString()} units`} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="harvest"
                  stroke="#4CAF50"
                  name="Harvest"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="sales"
                  stroke="#2196F3"
                  name="Sales"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
 
          {/* Revenue Trend */}
          <div className="widget">
            <div className="widget-header">
              <h2 className="h22">Revenue Trend</h2>
            </div>
 
            <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
              <LineChart data={revenueTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={(tick) => tick} />
                <YAxis tickFormatter={formatNumber} />
                <Tooltip formatter={(val) => `₹${formatNumber(val)}`} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke="#FF9800"
                  name="Revenue"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
 
          {/* Inventory Health */}
          <div className="widget">
            <div
              className="d-flex align-items-center justify-content-between"
              style={{ maxHeight: "60px" }}
            >
              <h2 className="h22">Inventory Health</h2>
              <div className="inventory-status">
                {kpi.inventory > 50 ? (
                  <p className="overstock">🔴 Overstock</p>
                ) : kpi.inventory > 20 ? (
                  <p className="balanced">🟡 Balanced</p>
                ) : (
                  <p className="understock">🟢 Understock</p>
                )}
              </div>
            </div>
 
            <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
              <BarChart
                data={[{ name: "Inventory", value: 70 }]}
                layout="vertical"
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  type="number"
                  domain={[0, Math.max(100, kpi.inventory + 20)]}
                />
                <YAxis type="category" dataKey="name" />
                <Tooltip formatter={(val) => `${val}`} />
 
                <Legend />
                <Bar
                  dataKey="value"
                  fill={getInventoryColor()}
                  name="Inventory"
                >
                  <LabelList dataKey="value" position="right" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
 
          {/* Top 5 Crops*/}
          <div className="widget">
            <h2 className="h22">Top 5 Performing Crops</h2>
            <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
              <BarChart data={filteredTopCrops}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="product" />
                <YAxis tickFormatter={formatNumber} />
                <Tooltip formatter={(val) => `₹${formatNumber(val)}`} />
                <Legend />
                <Bar dataKey="revenue" fill="#f9a324" name="Revenue" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};
 
export default Dashboard;